import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    CardContent,
    CardMedia,
    CircularProgress,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
    Slide,
    AppBar,
    Toolbar,
    TextField,
    Slider,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../auth/AuthContext';
import { useNotification } from '../notification/NotificationProvider';
import { Stack } from '@mui/system';
import { useParams, useNavigate } from 'react-router-dom';

// Define a transition for the Dialog
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ActivitiesFeedScreen = () => {
    const { currentUser, dbUser, isLoading: authLoading } = useAuth();
    const { triggerNotification } = useNotification();
    const { activityTitle } = useParams();
    const navigate = useNavigate();

    const [activities, setActivities] = useState([]);
    const [hasDataProcessed, setHasDataProcessed] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [openImageDialog, setOpenImageDialog] = useState(false);

    // New state for filters
    const [filterName, setFilterName] = useState('');
    const [maxDistance, setMaxDistance] = useState(50); // Default max distance in km
    const [userLocation, setUserLocation] = useState(null); // To store user's current location

    // State for Accordion expanded/collapsed, controlled to be open by default
    const [expanded, setExpanded] = useState(true);

    // Use a ref to prevent multiple notifications for location failure
    const locationNotificationShown = useRef(false);

    // Effect to get user's current location on component mount
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                (error) => {
                    console.error("Error obtaining location: ", error);
                    if (!locationNotificationShown.current) {
                        triggerNotification({
                            message: `NO SE PUDO OBTENER TU UBICACIÓN. Mostrando actividades de una ubicación por defecto.`,
                            sound: true,
                            show: true,
                            type: 'tiny-top',
                            cat: 'location-error',
                            isError: true,
                            forceId: 'public-activities-useEffect-getuserlocation-error'
                        });
                        locationNotificationShown.current = true;
                    }
                    // Set a default location if geolocation fails or is denied (e.g., A Coruña coordinates)
                    setUserLocation({
                        lat: 43.3370257,
                        lng: -8.3704727,
                    });
                },
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
            );
        } else {
            if (!locationNotificationShown.current) {
                triggerNotification({
                    message: `GEOLOCALIZACIÓN NO SOPORTADA EN ESTE NAVEGADOR. Mostrando actividades de una ubicación por defecto.`,
                    sound: true,
                    show: true,
                    type: 'tiny-top',
                    cat: 'location-unsupported',
                    isError: true,
                    forceId: 'public-activities-useEffect-getuserlocation-unsupported'
                });
                locationNotificationShown.current = true;
            }
            // Set a default location if geolocation is not supported
            setUserLocation({
                lat: 43.3370257,
                lng: -8.3704727,
            });
        }
    }, [triggerNotification]); // Depend on triggerNotification to ensure it's stable

    // Effect for handling the image carousel index when dialog opens/closes
    useEffect(() => {
        if (openDialog) {
            setCurrentImageIndex(0);
        }
    }, [openDialog]); // Removed selectedActivity from dependency array as it's already implicitly tied to openDialog

    // Effect to fetch activities based on auth state, filters, and user location
    useEffect(() => {
        // Only fetch if authentication is not loading and dbUser is available, and userLocation is known
        if (!authLoading && dbUser && userLocation) {
            const fetchActivities = async () => {
                try {
                    let fetchedActivities = [];
                    // Ensure dbUser.getPublicActivities is a function before calling
                    if (typeof dbUser.getPublicActivities === 'function') {
                        fetchedActivities = await dbUser.getPublicActivities(filterName, maxDistance, userLocation);
                    } else if (dbUser.publicActivities) {
                        // Fallback if getPublicActivities is not a method (e.g., if it's directly an array/object)
                        if (Array.isArray(dbUser.publicActivities)) {
                            fetchedActivities = dbUser.publicActivities;
                        } else if (typeof dbUser.publicActivities === 'object') {
                            fetchedActivities = Object.values(dbUser.publicActivities);
                        }
                        // Apply client-side filtering if getPublicActivities is not a function
                        fetchedActivities = fetchedActivities.filter(activity => {
                            const matchesName = filterName ? activity.title.toLowerCase().includes(filterName.toLowerCase()) : true;
                            // Add distance filtering here if not done by dbUser.getPublicActivities
                            // For simplicity, this example assumes dbUser.getPublicActivities handles distance.
                            // If not, you'd need a calculateDistance function here.
                            return matchesName;
                        });
                    }
                    setActivities(fetchedActivities);

                    if (activityTitle) {
                        const decodedActivityTitle = decodeURIComponent(activityTitle).replace(/-/g, ' ');
                        const activityToOpen = fetchedActivities.find(
                            (activity) => activity.title.toLowerCase() === decodedActivityTitle.toLowerCase()
                        );
                        if (activityToOpen) {
                            setSelectedActivity(activityToOpen);
                            setOpenDialog(true);
                        } else {
                            console.warn(`Actividad "${decodedActivityTitle}" no encontrada.`);
                            navigate('/misbolos/activities'); // Redirect if activity not found
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch public activities:', error);
                    setActivities([]);
                    triggerNotification({
                        message: `Error al cargar actividades: ${error.message}`,
                        sound: true,
                        show: true,
                        type: 'tiny-top',
                        cat: 'fetch-error',
                        isError: true,
                        forceId: 'public-activities-useEffect-loadingActivities-error1'
                    });
                } finally {
                    setHasDataProcessed(true);
                }
            };

            // Debounce the fetchActivities call to avoid excessive API calls on slider/text input change
            const handler = setTimeout(() => {
                fetchActivities();
            }, 500); // 500ms debounce

            return () => {
                clearTimeout(handler);
            };
        } else if (!authLoading && !dbUser) {
            // If not authenticated, clear activities and mark as processed
            setActivities([]);
            setHasDataProcessed(true);
        }
    }, [authLoading, dbUser, activityTitle, navigate, filterName, maxDistance, userLocation, triggerNotification]); // Add all relevant dependencies

    const handleCardClick = (activity) => {
        const encodedActivityTitle = encodeURIComponent(activity.title).replace(/ /g, '-');
        navigate(`/misbolos/activities/${encodedActivityTitle}`);
        setSelectedActivity(activity);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedActivity(null);
        navigate('/misbolos/activities'); // Reset URL when dialog closes
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === selectedActivity.images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const handlePreviousImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? selectedActivity.images.length - 1 : prevIndex - 1
        );
    };

    const handleImageClick = () => {
        setOpenImageDialog(true);
    };

    const handleCloseImageDialog = () => {
        setOpenImageDialog(false);
    };

    const handleFilterNameChange = (event) => {
        setFilterName(event.target.value);
    };

    const handleMaxDistanceChange = (event, newValue) => {
        setMaxDistance(newValue);
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    // Show loading indicator until authentication is resolved and data is processed (including initial location)
    if (authLoading || !hasDataProcessed || !userLocation) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ marginLeft: 2 }}>Cargando actividades...</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                padding: 4
            }}
        >
            <Container maxWidth="lg">
                <Typography variant="h3" fontWeight="bold" align="center" gutterBottom>
                    Descubre bolos en tu zona
                </Typography>
                <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    Bolos y shows en tu zona...
                </Typography>

                {/* Filter Section */}
                <Accordion expanded={expanded === 'distancePanel'} onChange={handleAccordionChange('distancePanel')}
                    sx={{ mb: 4, borderRadius: 2, boxShadow: 3, bgcolor: 'background.paper' }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="distance-filter-content"
                        id="distance-filter-header"
                    >
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SearchIcon /> Filtros de Búsqueda
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'center' }}>
                            <TextField
                                label="Filtrar por nombre"
                                variant="outlined"
                                value={filterName}
                                onChange={handleFilterNameChange}
                                fullWidth
                                sx={{ flex: 1 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Box sx={{ width: { xs: '100%', md: 'auto' }, minWidth: 200, flex: 1 }}>
                                <Typography gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationOnIcon /> Distancia máxima: {maxDistance} km
                                </Typography>
                                <Slider
                                    value={maxDistance}
                                    onChange={handleMaxDistanceChange}
                                    aria-labelledby="max-distance-slider"
                                    valueLabelDisplay="auto"
                                    step={5}
                                    marks
                                    min={5}
                                    max={200}
                                    sx={{ width: '90%', ml: 1 }}
                                />
                            </Box>
                        </Box>
                    </AccordionDetails>
                </Accordion>
                {/* End Filter Section */}

                {activities.length === 0 ? (
                    <Box sx={{ textAlign: 'center', mt: 8 }}>
                        <Typography variant="h5" color="text.secondary">
                            No se encontraron actividades por el momento. ¡Vuelve pronto!
                        </Typography>
                        {!dbUser && (
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                                Por favor, inicia sesión para ver tus actividades públicas o asegúrate de que tu cuenta tenga actividades.
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Grid container spacing={4}>
                        {activities.map((activity) => (
                            <Grid item key={activity._id || activity.id} xs={12} sm={6} md={4} lg={3}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxShadow: 3,
                                        borderRadius: 2,
                                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: 6,
                                            cursor: 'pointer',
                                        },
                                    }}
                                    onClick={() => handleCardClick(activity)}
                                >
                                    {activity.images && activity.images.length > 0 && (
                                        <CardMedia
                                            component="img"
                                            height="180"
                                            image={`https://bluejims.com:5005/misbolos/image/${activity.images[0]}`}
                                            alt={activity.title}
                                            sx={{ borderTopLeftRadius: 8, borderTopRightRadius: 8 }}
                                        />
                                    )}
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography gutterBottom variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                                            {activity.title}
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                🗓️ {new Date(activity.date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                            </Typography>
                                            {activity.timerange && (
                                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                                    ⏰ {activity.timerange.start} - {activity.timerange.end === "NaN:NaN" ? "Fin no especificado" : activity.timerange.end}
                                                </Typography>
                                            )}
                                        </Box>

                                        {activity.price && (
                                            <Chip
                                                label={activity.price === "Gratis" ? "GRATIS" : `€${activity.price}`}
                                                color={activity.price === "Gratis" ? "success" : "primary"}
                                                size="small"
                                                sx={{ mb: 1, fontWeight: 'bold' }}
                                            />
                                        )}

                                        {activity.location && (
                                            <Typography variant="caption" color="text.hint" display="block" sx={{ mt: 1 }}>
                                                📍 {activity.location.localId || 'Ubicación Desconocida'}
                                                {activity.location.center && userLocation && (
                                                    ` (${calculateDistance(userLocation, activity.location.center).toFixed(1)} km)`
                                                )}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>

            {/* Activity Detail Dialog */}
            <Dialog
                fullScreen
                open={openDialog}
                onClose={handleCloseDialog}
                TransitionComponent={Transition}
            >
                <AppBar sx={{ position: 'relative', background: 'linear-gradient(to right,rgb(31, 31, 31),rgb(20, 20, 20))' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleCloseDialog}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Detalles del Bolo
                        </Typography>
                    </Toolbar>
                </AppBar>
                <DialogContent dividers sx={{ p: 0, '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
                    {selectedActivity && (
                        <Box sx={{ p: 3 }}>
                            {/* Image carousel with navigation arrows */}
                            {selectedActivity.images && selectedActivity.images.length > 0 && (
                                <Box sx={{ position: 'relative', mb: 3 }}>
                                    <CardMedia
                                        component="img"
                                        image={`https://bluejims.com:5005/misbolos/image/${selectedActivity.images[currentImageIndex]}`}
                                        alt={selectedActivity.title}
                                        sx={{
                                            maxHeight: 400,
                                            width: '100%',
                                            objectFit: 'cover',
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                        }}
                                        onClick={handleImageClick}
                                    />
                                    {selectedActivity.images.length > 1 && (
                                        <>
                                            <IconButton
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: 8,
                                                    transform: 'translateY(-50%)',
                                                    color: 'white',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                    },
                                                }}
                                                onClick={handlePreviousImage}
                                                aria-label="previous image"
                                            >
                                                <ArrowBackIosNewIcon />
                                            </IconButton>
                                            <IconButton
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    right: 8,
                                                    transform: 'translateY(-50%)',
                                                    color: 'white',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                    },
                                                }}
                                                onClick={handleNextImage}
                                                aria-label="next image"
                                            >
                                                <ArrowForwardIosIcon />
                                            </IconButton>
                                        </>
                                    )}
                                    {/* Image counter */}
                                    {selectedActivity.images.length > 0 && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                position: 'absolute',
                                                bottom: 8,
                                                right: 8,
                                                color: 'white',
                                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                borderRadius: 1,
                                                padding: '2px 6px',
                                            }}
                                        >
                                            {currentImageIndex + 1} / {selectedActivity.images.length}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#3f51b5' }}>
                                {selectedActivity.title}
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    🗓️ <Box component="span" sx={{ ml: 1, fontWeight: 'medium' }}>{new Date(selectedActivity.date).toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</Box>
                                </Typography>
                                {selectedActivity.timerange && (
                                    <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        ⏰ <Box component="span" sx={{ ml: 1, fontWeight: 'medium' }}>{selectedActivity.timerange.start} - {selectedActivity.timerange.end === "NaN:NaN" ? "Fin no especificado" : selectedActivity.timerange.end}</Box>
                                    </Typography>
                                )}
                                {selectedActivity.location && (
                                    <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        📍 <Box component="span" sx={{ ml: 1, fontWeight: 'medium' }}>{selectedActivity.location.localId || 'Ubicación Desconocida'}</Box>
                                        {selectedActivity.location.center && userLocation && (
                                            <Box component="span" sx={{ ml: 1, fontWeight: 'medium' }}>({calculateDistance(userLocation, selectedActivity.location.center).toFixed(1)} km)</Box>
                                        )}
                                    </Typography>
                                )}
                            </Box>

                            <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5' }}>
                                Precio:
                            </Typography>
                            <Chip
                                label={selectedActivity.price === "Gratis" ? "GRATIS" : `€${selectedActivity.price}`}
                                color={selectedActivity.price === "Gratis" ? "success" : "primary"}
                                sx={{ fontSize: '1rem', padding: '5px 10px', height: 'auto', fontWeight: 'bold' }}
                            />

                            <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5' }}>
                                Más detalles:
                            </Typography>
                            <Typography variant="body1" paragraph>
                                {selectedActivity.detailedInfo}
                            </Typography>

                            {selectedActivity.groups && selectedActivity.groups.length > 0 && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5' }}>
                                        Grupos Asociados:
                                    </Typography>
                                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                        {selectedActivity.groups.map((group, index) => (
                                            <Chip
                                                key={group.name || index}
                                                label={`${group.name}`}
                                                color="info"
                                                onClick={() => navigate(`/misbolos/groups/${group.name}`)}
                                                sx={{ backgroundColor: 'rgb(36, 36, 36)', color: 'white', fontWeight: 'bold', margin: '3px' }}
                                            />
                                        ))}
                                    </Stack>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Full-Screen Image Dialog */}
            {selectedActivity && selectedActivity.images && selectedActivity.images.length > 0 && (
                <Dialog
                    fullScreen
                    open={openImageDialog}
                    onClose={handleCloseImageDialog}
                    TransitionComponent={Transition}
                >
                    <AppBar sx={{ position: 'relative', background: 'black' }}>
                        <Toolbar>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={handleCloseImageDialog}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                                Imagen de {selectedActivity.title}
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <DialogContent sx={{
                        backgroundColor: 'black',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 0,
                        '&::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                    }}>
                        <img
                            src={`https://bluejims.com:5005/misbolos/image/${selectedActivity.images[currentImageIndex]}`}
                            alt={selectedActivity.title}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
};

export default ActivitiesFeedScreen;

// Helper function to calculate distance between two lat/lng points (Haversine formula)
const calculateDistance = (latLng1, latLng2) => {
    const R = 6371; // Radius of Earth in kilometers
    const dLat = (latLng2.lat - latLng1.lat) * Math.PI / 180;
    const dLon = (latLng2.lng - latLng1.lng) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(latLng1.lat * Math.PI / 180) * Math.cos(latLng2.lat * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in kilometers
    return distance;
};